import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Home, Mail, CheckCircle, Building, Sun, Leaf } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from './ui/carousel';

export const TufetschPage = () => {
  const navigate = useNavigate();

  const images = [
    "https://customer-assets.emergentagent.com/job_461c8c5b-3e0c-4d86-ba83-d0156d9ef57a/artifacts/zjauteen_Archsynth%20image%281%29%20copy%202.png",
    "https://customer-assets.emergentagent.com/job_tufetsch-residences/artifacts/50adsydw_archsynth_image%281%29.png",
    "https://customer-assets.emergentagent.com/job_750b5e79-a862-4cdd-b6c7-ee8f9da637e3/artifacts/dgr38nlm_Archsynth%20image%281%29%20copy%204.png",
    "https://customer-assets.emergentagent.com/job_750b5e79-a862-4cdd-b6c7-ee8f9da637e3/artifacts/zzxkbbop_Archsynth%20image%282%29%20copy%203.png",
    "https://customer-assets.emergentagent.com/job_tufetsch-residences/artifacts/qpvyvbm2_Archsynth%20image%286%29.png",
    "https://customer-assets.emergentagent.com/job_tufetsch-residences/artifacts/v96tpykp_Archsynth%20image%285%29.png",
    "https://customer-assets.emergentagent.com/job_750b5e79-a862-4cdd-b6c7-ee8f9da637e3/artifacts/c024pdwf_archsynth_image%285%29.png",
    "https://customer-assets.emergentagent.com/job_tufetsch-residences/artifacts/2eg22e1z_archsynth_image.png"
  ];

  const caracteristiques = [
    { label: "Appartements", value: "12 unités" },
    { label: "Pièces", value: "2.5 pièces" },
    { label: "Surface", value: "60.5 - 63.5 m²" },
    { label: "Immeubles", value: "2 bâtiments" },
    { label: "Étages", value: "3 niveaux" },
    { label: "Parking", value: "10 box + 10 ext." },
    { label: "Énergie", value: "PAC + Photovoltaïque" }
  ];

  const pointsForts = [
    "12 appartements de 2.5 pièces dans 2 immeubles élégants",
    "Terrasses et balcons spacieux",
    "Pompe à chaleur air-eau (PAC)",
    "Panneaux photovoltaïques",
    "Cadre naturel calme et verdoyant",
    "10 box couverts et 10 places extérieures",
    "Finitions haut de gamme",
    "Architecture moderne et lumineuse"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-[#1e5a7a] text-white py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-[#f5b800] mb-6"
          >
            <ArrowLeft className="mr-2" size={20} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Tufetsch Résidences
          </h1>
          <div className="flex flex-wrap gap-4 text-lg">
            <div className="flex items-center">
              <MapPin className="mr-2" size={20} />
              Tourtemagne, Valais
            </div>
            <div className="flex items-center">
              <Building className="mr-2" size={20} />
              12 appartements neufs
            </div>
          </div>
        </div>
      </div>

      {/* Carrousel d'images */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <Carousel className="w-full">
            <CarouselContent>
              {images.map((image, index) => (
                <CarouselItem key={index}>
                  <div className="relative h-[500px] md:h-[600px] overflow-hidden rounded-2xl">
                    <img
                      src={image}
                      alt={`Tufetsch Résidences - Image ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-4" />
            <CarouselNext className="right-4" />
          </Carousel>
        </div>
      </section>

      {/* Description */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-8">
              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Une nouvelle adresse résidentielle au coeur du Valais
                  </h2>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Le projet <strong>Tufetsch Résidences</strong> propose un cadre de vie rare à Tourtemagne, dans un environnement calme et verdoyant.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed mb-4">
                    Deux immeubles élégants de trois étages accueillent <strong>12 appartements de 2.5 pièces</strong>. Les surfaces habitables brutes varient de 60.5 à 63.5 m² avec terrasses ou balcons spacieux.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Points Forts
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {pointsForts.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <CheckCircle size={24} className="text-[#f5b800] mr-3 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-lg">{item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#1e5a7a] mb-6">
                    Intérieurs Raffinés
                  </h2>
                  <p className="text-lg text-gray-700 mb-6">
                    Des finitions haut de gamme pour un confort absolu :
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Home size={32} className="text-[#1e5a7a] mx-auto mb-2" />
                      <p className="font-medium">Cuisine ouverte</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Sun size={32} className="text-[#1e5a7a] mx-auto mb-2" />
                      <p className="font-medium">Séjour lumineux</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Leaf size={32} className="text-[#1e5a7a] mx-auto mb-2" />
                      <p className="font-medium">Salle de bain premium</p>
                    </div>
                    <div className="text-center p-4 bg-gray-50 rounded-xl">
                      <Building size={32} className="text-[#1e5a7a] mx-auto mb-2" />
                      <p className="font-medium">Chambre spacieuse</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <Card className="border-0 shadow-xl bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white sticky top-8">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-6 text-[#f5b800]">
                    Informations
                  </h3>
                  <div className="space-y-4 mb-8">
                    {caracteristiques.map((item, index) => (
                      <div key={index} className="border-b border-white/20 pb-3">
                        <p className="text-sm text-white/70 mb-1">{item.label}</p>
                        <p className="text-lg font-semibold">{item.value}</p>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/20">
                    <h4 className="font-semibold mb-3 text-[#f5b800]">Contact</h4>
                    <div className="flex items-start">
                      <Mail className="mr-2 flex-shrink-0 mt-1" size={18} />
                      <a href="mailto:guillaume@immoconceptsuisse.com" className="text-white hover:text-[#f5b800] transition-colors break-all">
                        guillaume@immoconceptsuisse.com
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-[#1e5a7a] to-[#15475c] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Intéressé par ce projet ?
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Contactez-nous pour plus d'informations ou pour planifier une visite.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={() => navigate('/#contact')}
              size="lg"
              className="bg-[#f5b800] hover:bg-[#d9a400] text-black font-semibold px-8 py-6 text-lg"
            >
              Nous contacter
            </Button>
            <Button
              onClick={() => window.open('mailto:guillaume@immoconceptsuisse.com', '_blank')}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-[#1e5a7a] font-semibold px-8 py-6 text-lg"
            >
              Envoyer un email
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
